/**
 * Reads cached Convex JWT from localStorage (set by @convex-dev/better-auth's
 * cross-domain plugin) so ConvexBetterAuthProvider can auth the WebSocket on
 * first render without an HTTP round-trip. Returns null on any failure/expiry
 * (provider falls back to its normal fetch flow).
 *
 * Depends on plugin's storage format: key "better-auth_cookie" -> JSON
 * { "<cookie-name>": { value, expires } }; cookie name is
 * "__Secure-better-auth.convex_jwt" (HTTPS) or "better-auth.convex_jwt" (HTTP/localhost).
 */

const STORAGE_KEY = "better-auth_cookie";
const SECURE_COOKIE = "__Secure-better-auth.convex_jwt";
const PLAIN_COOKIE = "better-auth.convex_jwt";

/** Minimum seconds remaining before we consider a cached JWT usable. */
const MIN_REMAINING_SECONDS = 60;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
	const parts = token.split(".");
	if (parts.length !== 3) return null;
	let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
	while (base64.length % 4 !== 0) base64 += "=";
	const json = atob(base64);
	const parsed: unknown = JSON.parse(json);
	if (typeof parsed === "object" && parsed !== null) {
		return parsed as Record<string, unknown>;
	}
	return null;
}

export function getCachedConvexToken(): string | null {
	try {
		if (typeof window === "undefined" || !window.localStorage) return null;

		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;

		const cookies: unknown = JSON.parse(raw);
		if (typeof cookies !== "object" || cookies === null) return null;

		// Try secure-prefixed key first (production), then plain (dev HTTP)
		const entry =
			(SECURE_COOKIE in cookies
				? (cookies as Record<string, unknown>)[SECURE_COOKIE]
				: undefined) ??
			(PLAIN_COOKIE in cookies
				? (cookies as Record<string, unknown>)[PLAIN_COOKIE]
				: undefined);

		if (
			typeof entry !== "object" ||
			entry === null ||
			!("value" in entry) ||
			typeof (entry as Record<string, unknown>).value !== "string"
		) {
			return null;
		}

		const jwt = (entry as Record<string, unknown>).value as string;

		// Check the cookie-level expiry stored by the cross-domain plugin
		if ("expires" in entry) {
			const expires = (entry as Record<string, unknown>).expires;
			if (typeof expires === "string" && new Date(expires) < new Date()) {
				return null;
			}
		}

		// Decode the JWT and check the `exp` claim with a safety buffer
		const payload = decodeJwtPayload(jwt);
		if (!payload) return null;

		const exp = payload.exp;
		if (typeof exp !== "number") return null;

		const nowSeconds = Date.now() / 1000;
		if (exp < nowSeconds + MIN_REMAINING_SECONDS) return null;

		return jwt;
	} catch {
		return null;
	}
}
